import React, { useState, useEffect } from 'react'
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material'
import {
  Refresh,
  Visibility,
  Warning,
  Schedule,
  Assignment,
  FilterList
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import api from '../services/api'

export const OverdueDeficienciesPage = () => {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  
  const [loading, setLoading] = useState(false)
  const [overdueFiles, setOverdueFiles] = useState([])
  const [stats, setStats] = useState({})
  const [filters, setFilters] = useState({
    department: '',
    severity: '',
    daysOverdue: ''
  })

  useEffect(() => {
    loadOverdueDeficiencies()
  }, [])

  const loadOverdueDeficiencies = async () => {
    setLoading(true)
    try {
      const response = await api.get('/mrd/deficiency/overdue/list', {
        params: filters
      })
      
      if (response.data.success) {
        setOverdueFiles(response.data.data.files || [])
        setStats(response.data.data.stats || {})
      }
    } catch (error) {
      enqueueSnackbar('Failed to load overdue deficiencies', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }))
  }

  const applyFilters = () => {
    loadOverdueDeficiencies()
  }

  const resetFilters = () => {
    setFilters({ department: '', severity: '', daysOverdue: '' })
    setTimeout(loadOverdueDeficiencies, 100)
  }

  const getDaysOverdue = (dueDate) => {
    const today = new Date()
    const due = new Date(dueDate)
    const diffTime = today - due
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  }

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'HIGH': return 'error'
      case 'MEDIUM': return 'warning'
      case 'LOW': return 'info'
      default: return 'default'
    }
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Overdue Deficiencies
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Files with deficiencies past their due dates
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadOverdueDeficiencies}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Warning color="error" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4" color="error" fontWeight={600}>
                    {stats.totalOverdue || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Overdue Files
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Schedule color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4" color="warning.main" fontWeight={600}>
                    {stats.avgDaysOverdue || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg Days Overdue
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Assignment color="error" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4" color="error" fontWeight={600}>
                    {stats.criticalOverdue || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Critical Overdue
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Schedule color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4" color="info.main" fontWeight={600}>
                    {stats.longestOverdue || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Longest Overdue (days)
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Department"
              value={filters.department}
              onChange={(e) => handleFilterChange('department', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Severity</InputLabel>
              <Select
                value={filters.severity}
                label="Severity"
                onChange={(e) => handleFilterChange('severity', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="HIGH">High</MenuItem>
                <MenuItem value="MEDIUM">Medium</MenuItem>
                <MenuItem value="LOW">Low</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Days Overdue (min)"
              value={filters.daysOverdue}
              onChange={(e) => handleFilterChange('daysOverdue', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1, height: '100%', alignItems: 'center' }}>
              <Button
                variant="contained"
                startIcon={<FilterList />}
                onClick={applyFilters}
                fullWidth
              >
                Apply
              </Button>
              <Button
                variant="outlined"
                onClick={resetFilters}
                fullWidth
              >
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Overdue Files Table */}
      <Paper>
        {loading && <LinearProgress />}
        
        {overdueFiles.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Alert severity="success">
              No overdue deficiencies found!
            </Alert>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>File Number</TableCell>
                  <TableCell>Patient Name</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Deficiency Type</TableCell>
                  <TableCell>Severity</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Days Overdue</TableCell>
                  <TableCell>Assigned To</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overdueFiles.map((file) => (
                  <TableRow key={file._id} hover>
                    <TableCell>
                      <Chip
                        label={file.fileNumber}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {file.patientName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {file.department?.name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {file.deficiencyType}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={file.severity}
                        size="small"
                        color={getSeverityColor(file.severity)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(file.dueDate).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${getDaysOverdue(file.dueDate)} days`}
                        size="small"
                        color="error"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {file.assignedTo?.name || 'Unassigned'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Deficiency Checklist">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/deficiency/${file._id}`)}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Container>
  )
}

